import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { T } from "../../constants/theme";
import { API } from "../../constants/config";
import { useApi } from "../../hooks/useApi";
import { normalizeCategories } from "../../utils/categoryHelpers";

export default function CategoryGrid() {
  const navigate = useNavigate();
  const { data, loading } = useApi("/categories");

  const categories = useMemo(
    () => normalizeCategories(data?.data || data?.categories || data),
    [data]
  );

  const [categoryImageMap, setCategoryImageMap] = useState({});
  const scrollRef = useRef(null);

  const scrollByCards = (direction) => {
    const track = scrollRef.current;
    if (!track) return;
    const card = track.querySelector("[data-category-card]");
    const step = card ? card.offsetWidth + 24 : track.clientWidth * 0.8;
    track.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  useEffect(() => {
  if (!categories.length) return;

  let isMounted = true;

  const fetchImages = async () => {
    const results = await Promise.all(
      categories.map(async (cat) => {
        try {
          const res = await fetch(
            `${API}/images/category/${cat.id}`
          );

          if (!res.ok) return [cat.id, null];

          const data = await res.json();
          return [cat.id, data.url || null];
        } catch {
          return [cat.id, null];
        }
      })
    );

    if (isMounted) {
      setCategoryImageMap(Object.fromEntries(results));
    }
  };

  fetchImages();

  return () => {
    isMounted = false;
  };
}, [categories]);

  return (
    
    <section
      style={{
        background: T.paper,
        padding: "90px 0",
      }}
    >
      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: "0 32px",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            textAlign: "center",
            marginBottom: 50,
          }}
        >
          <span
            style={{
              color: T.gold,
              textTransform: "uppercase",
              letterSpacing: ".15em",
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            Browse Collections
          </span>

          <h2
            style={{
              fontFamily: "'Georgia', serif",
              fontSize: 42,
              color: T.ink,
              margin: "12px 0",
            }}
          >
            Shop By Category
          </h2>

          <p
            style={{
              color: T.muted,
              fontSize: 16,
              maxWidth: 600,
              margin: "0 auto",
              lineHeight: 1.7,
            }}
          >
            Discover Gardening Tips and Tools For All Kind Of
          </p>

          <div
            style={{
              width: 70,
              height: 2,
              background: T.gold,
              margin: "24px auto 0",
            }}
          />
        </div>

        {/* LOADING */}
        {loading ? (
          <div
            style={{
              textAlign: "center",
              color: T.muted,
            }}
          >
            Loading categories...
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            {/* LEFT SCROLL BUTTON */}
            <button
              type="button"
              onClick={() => scrollByCards(-1)}
              aria-label="Scroll categories left"
              style={{
                position: "absolute",
                top: "50%",
                left: -22,
                transform: "translateY(-50%)",
                zIndex: 2,
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: "none",
                background: T.white,
                boxShadow: T.shadowSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: T.ink,
                fontSize: 20,
              }}
            >
              <FiChevronLeft />
            </button>

            {/* SCROLL TRACK */}
            <div
              ref={scrollRef}
              className="category-scroll-track"
              style={{
                display: "flex",
                gap: 24,
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                scrollBehavior: "smooth",
                WebkitOverflowScrolling: "touch",
                padding: "4px 4px 12px",
              }}
            >
              {categories.map((cat) => {
                const image = categoryImageMap[cat.id];

                return (
                  <div
                    key={cat.id}
                    data-category-card
                    onClick={() =>
                      navigate(`/shop?cat=${cat.slug || ""}`)
                    }
                    style={{
                      position: "relative",
                      flex: "0 0 auto",
                      width: 320,
                      height: 420,
                      borderRadius: 24,
                      overflow: "hidden",
                      cursor: "pointer",
                      background: "#f5f5f5",
                      scrollSnapAlign: "start",
                    }}
                  >
                    {/* IMAGE OR FALLBACK */}
                    {image ? (
                      <img
                        src={image}
                        alt={cat.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform .5s ease",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#aaa",
                          fontSize: 14,
                          background: "#eee",
                        }}
                      >
                        No image available
                      </div>
                    )}

                    {/* DARK OVERLAY */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(to top, rgba(0,0,0,.75), rgba(0,0,0,.15))",
                      }}
                    />

                    {/* TEXT */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: 30,
                        left: 30,
                        color: "#fff",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 32,
                          fontWeight: 700,
                          fontFamily: "'Georgia', serif",
                          marginBottom: 8,
                        }}
                      >
                        {cat.name}
                      </div>

                      <div
                        style={{
                          fontSize: 14,
                          letterSpacing: ".05em",
                          textTransform: "uppercase",
                        }}
                      >
                        Shop Category →
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* RIGHT SCROLL BUTTON */}
            <button
              type="button"
              onClick={() => scrollByCards(1)}
              aria-label="Scroll categories right"
              style={{
                position: "absolute",
                top: "50%",
                right: -22,
                transform: "translateY(-50%)",
                zIndex: 2,
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: "none",
                background: T.white,
                boxShadow: T.shadowSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: T.ink,
                fontSize: 20,
              }}
            >
              <FiChevronRight />
            </button>

            <style>
              {`
              .category-scroll-track::-webkit-scrollbar { display: none; }
              .category-scroll-track { scrollbar-width: none; }
              `}
            </style>
          </div>
        )}
      </div>
    </section>
  );
}
